import React from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';
import chroma from 'chroma-js';

import { motion } from '@ubisend/framer-motion';

import { Divider } from '../Spacing';
import { Placeholder } from '../Loading';
import { Heading2 } from '../Text';
import { grid } from '../styles';

const Container = styled(motion.div)`
  ${grid}
  ${tw`border border-solid border-gray-300 rounded w-full p-4 bg-white`};
  box-sizing: border-box;
  box-shadow: ${({ theme }) => chroma(theme.black).alpha(0.25)} 0px 10px 10px -10px;
  ${props => props.tall && tw`h-full`}
  & h2 {
    ${tw`font-semibold text-xl`}
  }
  & > h2 {
    ${tw`m-0 p-0 mb-4`}
  }
`;

const CommentsContainer = styled.div`
  ${grid}
  ${tw`border border-solid border-gray-300 rounded w-full p-4 bg-white`};
  box-sizing: border-box;
  box-shadow: ${({ theme }) => chroma(theme.black).alpha(0.25)} 0px 10px 10px -10px;
  ${props => props.tall && tw`h-full`}
  & h2 {
    ${tw`font-semibold text-xl`}
  }
  & > h2 {
    ${tw`m-0 p-0 mb-4`}
  }
`;

const HeaderContainer = styled.div`
  ${tw`flex items-center border-b`}
`;

const ActionsContainer = styled.div`
  ${tw`flex p-4 md:p-0`}
`;

const Grow = styled.div`
  ${tw`flex-grow`}
`;

const Panel = React.forwardRef(
  (
    {
      header,
      actions,
      children,
      divider = false,
      loading = false,
      loadingItems = 2,
      comments,
      ...props
    },
    ref
  ) => {
    if (comments) {
      if (header && actions) {
        return (
          <CommentsContainer {...props} ref={ref}>
            <HeaderContainer>
              <Heading2>{header}</Heading2>
              <Grow />
              <ActionsContainer>{actions}</ActionsContainer>
            </HeaderContainer>
            {divider && <Divider fullWidth />}
            {loading ? <Placeholder /> : children}
          </CommentsContainer>
        );
      }

      return (
        <CommentsContainer {...props} ref={ref}>
          {header && <Heading2>{header}</Heading2>}
          {divider && <Divider fullWidth />}
          {loading ? <Placeholder items={loadingItems} /> : children}
        </CommentsContainer>
      );
    }

    if (header && actions) {
      return (
        <Container {...props} ref={ref}>
          <HeaderContainer>
            <Heading2>{header}</Heading2>
            <Grow />
            <ActionsContainer>{actions}</ActionsContainer>
          </HeaderContainer>
          {divider && <Divider fullWidth />}
          {loading ? <Placeholder /> : children}
        </Container>
      );
    }

    return (
      <Container {...props} ref={ref}>
        {header && <Heading2>{header}</Heading2>}
        {divider && <Divider fullWidth />}
        {loading ? <Placeholder items={loadingItems} /> : children}
      </Container>
    );
  }
);

Panel.propTypes = {
  header: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  actions: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
  divider: PropTypes.bool,
  tall: PropTypes.bool,
  loading: PropTypes.bool,
  loadingItems: PropTypes.number,
  comments: PropTypes.bool
};

Panel.displayName = 'Panel';

export default Panel;
